import history from '../history';
import {
  celebrateWin,
  confirmAdvance,
  confirmCollect,
  confirmLoss,
  confirmWhoops,
  motionOk,
} from '../feedback';
import { getAdventureMusicEngine } from '../audio/adventureMusicEngine';
import {
  DEFAULT_DIFFICULTY,
  DIFFICULTY_ORDER,
  getDifficulty,
  readHighScore,
  storeDifficulty,
  writeHighScore,
} from './difficultyConfig';
import {
  createCreature,
  drawCreature,
  drawDirt,
  drawProcBird,
  drawProcCoin,
  isNearCreature,
  pickFreeColumn,
  tipY,
  tickCreature,
  updateDirt,
} from './groundCreatures';
import {
  createWeatherState,
  difficultyHasWind,
  drawWeather,
  ensureWeather,
  resetWeather,
  tickAmbientWind,
  tickStorm,
  tickWindGust,
} from './weatherFx';
import {
  createRewardFlower,
  drawRewardFlower,
  isNearRewardFlower,
  tickRewardFlower,
} from './groundPickups';
import {
  createLighthouse,
  drawBirdBeaconGlow,
  drawLighthouse,
  tickLighthouse,
} from './lighthouseSanctuary';

const BIRD_BOB_SPEED = 0.08;
const BIRD_BOB_AMP = 5;
const BIRD_SCALE_SPEED = 0.12;
const BIRD_SCALE_AMP = 0.04;
const BIRD_WON_KEY = 'wesam-bird-won';
const CELEBRATION_MS = 5200;
const SHAKE_MS = 280;
const FLASH_MS = 180;
const RECOIL_MS = 220;
const GAME_OVER_MS = 1600;
/** Horizontal scene slide when advancing easy → medium → hard */
const SEGMENT_SLIDE_MS = 880;
const FIREWORK_COLORS = [
  [255, 90, 95],
  [255, 190, 70],
  [120, 220, 130],
  [90, 180, 255],
  [210, 130, 255],
  [255, 255, 255],
];

export default function sketch(p) {
  let boardW = 800;
  let boardH = 400;
  let xValue = 40;
  let yValue = 80;
  let facing = 1;
  let birdW = 56;
  let birdH = 48;
  let birdGrowth = 0; // worms eaten → bird grows
  let birdGrowUntil = 0;
  const BIRD_GROWTH_MAX = 8;
  const BIRD_GROWTH_STEP = 0.09;
  let coinSize = 48;

  // Coins: simple alive flags + positions (procedural, no images)
  let coins = [];

  let nextPage;
  let gameWon = false;
  let extension = false;
  let home = false;
  let contained = false;
  let didAdvance = false;
  let celebrated = false;
  let celebrationUntil = 0;
  let sparks = [];
  let nextBurstAt = 0;

  let difficulty = getDifficulty(DEFAULT_DIFFICULTY);
  let segmentIndex = Math.max(0, DIFFICULTY_ORDER.indexOf(difficulty.id));
  let score = 0;
  let combo = 0;
  let lastCollectAt = 0;
  let highScore = readHighScore();
  let progressiveBonus = 0;
  let nextProgressiveAt = 0;
  let playStartedAt = 0;
  let onDifficultyChange = null;

  let worms = [];
  let snakes = [];
  let rewardFlowers = [];
  let lighthouse = null;
  let beamIlluminated = false;
  let dirt = [];
  let nextWormAt = 0;
  let nextSnakeAt = 0;
  let nextFlowerAt = 0;
  let shakeUntil = 0;
  let flashUntil = 0;
  let recoilUntil = 0;
  let recoilDx = 0;
  let recoilDy = 0;
  let zapCount = 0;
  let gameOverUntil = 0;
  let isGameOver = false;
  let weather = createWeatherState();
  /** Incoming segment atmosphere while the scene slides */
  let incomingWeather = null;
  /** @type {null | {
   *   nextIndex: number,
   *   nextDiff: object,
   *   startAt: number,
   *   duration: number,
   *   birdFromX: number,
   *   birdToX: number,
   * }} */
  let slide = null;

  function measureBoard() {
    if (contained) {
      const frame = document.querySelector('.home-game-frame');
      if (frame && frame.clientWidth > 0 && frame.clientHeight > 0) {
        return {
          w: Math.floor(frame.clientWidth),
          h: Math.floor(frame.clientHeight),
        };
      }
      return { w: Math.max(boardW, 640), h: Math.max(boardH, 320) };
    }
    let h = window.innerHeight;
    if (extension) h *= 1.65;
    return { w: window.innerWidth, h: Math.floor(h) };
  }

  function syncBoardSize(forceLayout) {
    const { w, h } = measureBoard();
    const changed = Math.abs(w - boardW) >= 1 || Math.abs(h - boardH) >= 1;
    if (!changed && !forceLayout) return;
    boardW = w;
    boardH = h;
    const ready = p._renderer && p.canvas;
    if (ready && (p.width !== w || p.height !== h)) {
      p.resizeCanvas(w, h);
      p.noStroke();
    }
    layoutPlayfield();
  }

  function layoutPlayfield() {
    // Fixed, readable sizes inside the board (aspect locked by procedural draw)
    birdH = Math.min(52, Math.max(40, Math.floor(boardH * 0.14)));
    birdW = Math.round(birdH * 1.15);
    coinSize = Math.min(52, Math.max(36, Math.floor(boardH * 0.12)));

    const coinRowY = Math.floor(boardH * 0.26 - coinSize / 2);
    const cols = boardW >= 900 ? 5 : 3;
    const left = boardW * 0.2;
    const right = boardW * 0.8;
    const gap = (right - left) / (cols - 1);

    const prev = coins;
    coins = [];
    for (let i = 0; i < cols; i += 1) {
      const wasAlive = prev[i] ? prev[i].alive : true;
      coins.push({
        x: left + gap * i,
        y: coinRowY,
        phase: i * 1.2,
        alive: home ? wasAlive : true,
      });
    }

    if (!playStartedAt) {
      xValue = Math.floor(boardW * 0.08);
      yValue = coinRowY;
    }
  }

  function creatureGroundY() {
    return Math.floor(boardH * 0.88);
  }

  function birdDrawSize() {
    const growth =
      1 + Math.min(birdGrowth, BIRD_GROWTH_MAX) * BIRD_GROWTH_STEP;
    let pulse = 1;
    const now = Date.now();
    if (now < birdGrowUntil) {
      const k = (birdGrowUntil - now) / 420;
      pulse = 1 + 0.18 * Math.sin((1 - k) * Math.PI);
    }
    return {
      w: birdW * growth * pulse,
      h: birdH * growth * pulse,
    };
  }

  function birdCenter() {
    const s = birdDrawSize();
    return { x: xValue + s.w * 0.5, y: yValue + s.h * 0.5 };
  }

  function hitPadCoin() {
    // Bird + coin radii so a visual touch counts; floor from difficulty pad
    const size = birdDrawSize();
    const birdR = Math.max(size.w, size.h) * 0.42;
    const coinR = coinSize * 0.5;
    const fromSizes = birdR + coinR + 6;
    const fromConfig = difficulty.hitPad || 0;
    return Math.max(fromSizes, fromConfig * 0.55, coinSize * 0.9);
  }

  function hitPadCreature() {
    return Math.max(48, Math.min(boardW, boardH) * 0.12);
  }

  function coinCenter(coin) {
    return { x: coin.x + coinSize * 0.5, y: coin.y + coinSize * 0.5 };
  }

  function distPointToSegment(px, py, x1, y1, x2, y2) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len2 = dx * dx + dy * dy;
    if (len2 < 0.0001) return Math.hypot(px - x1, py - y1);
    let t = ((px - x1) * dx + (py - y1) * dy) / len2;
    if (t < 0) t = 0;
    else if (t > 1) t = 1;
    return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
  }

  function isNearCoin(coin) {
    const b = birdCenter();
    const c = coinCenter(coin);
    return Math.hypot(b.x - c.x, b.y - c.y) <= hitPadCoin();
  }

  /** True if the bird's move path passed through the coin (prevents step-overs). */
  function pathHitsCoin(coin, fromX, fromY, toX, toY) {
    const c = coinCenter(coin);
    const size = birdDrawSize();
    const hx = size.w * 0.5;
    const hy = size.h * 0.5;
    return (
      distPointToSegment(c.x, c.y, fromX + hx, fromY + hy, toX + hx, toY + hy) <=
      hitPadCoin()
    );
  }

  function collectCoins(fromX, fromY) {
    if (!home || isGameOver || slide) return;
    for (let i = 0; i < coins.length; i += 1) {
      const coin = coins[i];
      if (!coin.alive) continue;
      const touched =
        isNearCoin(coin) ||
        (fromX != null &&
          fromY != null &&
          pathHitsCoin(coin, fromX, fromY, xValue, yValue));
      if (!touched) continue;
      coin.alive = false;
      onCoinCollected(coin.x, coin.y);
    }
  }

  function moveStepX() {
    return boardW / difficulty.moveDivisor;
  }

  function moveStepY() {
    return boardH / difficulty.moveDivisor;
  }

  function ensurePlayClock() {
    if (!playStartedAt) {
      playStartedAt = Date.now();
      nextProgressiveAt = playStartedAt + difficulty.progressiveIntervalMs;
    }
  }

  function spawnCreature(kind) {
    const living = worms.concat(snakes).concat(rewardFlowers);
    const columnIndex = pickFreeColumn(living, 5);
    return createCreature(boardW, boardH, kind, {
      columnIndex,
      columns: 5,
      groundY: creatureGroundY() + (Math.random() - 0.5) * 14,
    });
  }

  function spawnRewardFlower() {
    const living = worms.concat(snakes).concat(rewardFlowers);
    const columnIndex = pickFreeColumn(living, 5);
    return createRewardFlower(boardW, boardH, {
      columnIndex,
      columns: 5,
      groundY: creatureGroundY() + (Math.random() - 0.5) * 10,
    });
  }

  function ensureLighthouse() {
    if (!home || !difficulty.hasLighthouse) {
      lighthouse = null;
      return;
    }
    const desiredX = boardW * 0.82;
    const groundY = creatureGroundY() + 8;
    if (!lighthouse) {
      lighthouse = createLighthouse(boardW, boardH, {
        groundY,
        x: desiredX,
      });
    } else {
      lighthouse.groundY = groundY;
      lighthouse.x = desiredX;
      lighthouse.towerW = Math.max(36, boardW * 0.055);
      lighthouse.towerH = boardH * 0.72;
      lighthouse.beamRange = Math.max(280, boardW * 0.7);
    }
  }

  function ensureCreatures() {
    if (!home) return;
    const now = Date.now();
    if (!nextWormAt) {
      nextWormAt = now + 500;
      nextSnakeAt = now + (difficulty.id === 'easy' ? 8000 : 1200);
      nextFlowerAt = now + 1200;
    }
    if (worms.length === 0 && snakes.length === 0) {
      if (difficulty.id === 'hard') {
        snakes.push(spawnCreature('snake'));
      } else {
        worms.push(spawnCreature('worm'));
      }
    }
    ensureLighthouse();
  }

  function bumpHighScore() {
    if (score > highScore) {
      highScore = score;
      writeHighScore(score);
    }
  }

  function onCoinCollected(cx, cy) {
    ensurePlayClock();
    const now = Date.now();
    if (lastCollectAt && now - lastCollectAt <= difficulty.comboWindowMs) {
      combo += 1;
    } else {
      combo = 1;
    }
    lastCollectAt = now;
    const base = 10 + progressiveBonus;
    const comboExtra = (combo - 1) * difficulty.comboBonus;
    score += Math.round((base + comboExtra) * difficulty.scoreMultiplier);
    bumpHighScore();
    confirmCollect();
    if (motionOk()) {
      spawnBurst(
        cx + coinSize / 2,
        cy + coinSize / 2,
        difficulty.collectBurstCount + Math.min(combo, 6)
      );
    }
  }

  function onWormCollected(worm) {
    ensurePlayClock();
    worm.hit = true;
    worm.state = 'happy';
    worm.stateAt = Date.now();
    worm.sparkle = 0;
    worm.appleLift = 0;
    score += Math.round(difficulty.wormPoints * difficulty.scoreMultiplier);
    bumpHighScore();
    if (birdGrowth < BIRD_GROWTH_MAX) {
      birdGrowth += 1;
      birdGrowUntil = Date.now() + 420;
    }
    confirmCollect();
    if (motionOk()) {
      spawnBurst(worm.x, tipY(worm), difficulty.collectBurstCount + 4);
    }
  }

  function onFlowerCollected(flower) {
    ensurePlayClock();
    flower.hit = true;
    flower.state = 'happy';
    flower.stateAt = Date.now();
    flower.sparkle = 0;
    flower.appleLift = 0;
    const pts = difficulty.flowerPoints || 50;
    score += Math.round(pts * difficulty.scoreMultiplier);
    bumpHighScore();
    confirmCollect();
    if (motionOk()) {
      spawnBurst(
        flower.x,
        flower.groundY - flower.height * flower.progress,
        difficulty.collectBurstCount + 4
      );
    }
  }

  /** Hard lighthouse: beam pass over bird recovers one zap. */
  function onBeamRecovery() {
    if (!lighthouse || isGameOver || difficulty.maxZaps <= 0) return;
    if (zapCount <= 0) {
      // Warmth still lands, but nothing to restore
      return;
    }
    zapCount = Math.max(0, zapCount - 1);
    confirmCollect();
  }

  function restartRun() {
    score = 0;
    combo = 0;
    lastCollectAt = 0;
    progressiveBonus = 0;
    playStartedAt = 0;
    nextProgressiveAt = 0;
    birdGrowth = 0;
    birdGrowUntil = 0;
    zapCount = 0;
    isGameOver = false;
    gameOverUntil = 0;
    slide = null;
    incomingWeather = null;
    // Hard wipe: send the player back to the start of the adventure
    segmentIndex = 0;
    difficulty = getDifficulty(DIFFICULTY_ORDER[0]);
    clearSegmentEntities();
    sparks = [];
    facing = 1;
    resetWeather(weather);
    reviveCoins();
    layoutPlayfield();
    reviveCoins();
    ensureWeather(weather, difficulty, boardW, boardH);
    ensureCreatures();
    notifyDifficulty(difficulty.id);
  }

  function registerZap(now) {
    if (difficulty.maxZaps <= 0 || isGameOver) return;
    zapCount += 1;
    if (zapCount >= difficulty.maxZaps) {
      isGameOver = true;
      gameOverUntil = now + GAME_OVER_MS;
      confirmLoss();
    }
  }

  function onLightningHit() {
    if (isGameOver) return;
    ensurePlayClock();
    const now = Date.now();
    confirmWhoops();
    shakeUntil = now + SHAKE_MS;
    flashUntil = now + FLASH_MS;
    recoilDx = (Math.random() - 0.5) * 18;
    recoilDy = 10 + Math.random() * 8;
    recoilUntil = now + RECOIL_MS;
    xValue += recoilDx;
    yValue = Math.min(yValue + recoilDy, boardH - birdDrawSize().h * 0.9);
    registerZap(now);
  }

  function onSnakeHit(snake) {
    if (isGameOver) return;
    ensurePlayClock();
    snake.hit = true;
    snake.state = 'bite';
    snake.stateAt = Date.now();
    score = Math.max(0, score - difficulty.snakePenalty);
    confirmWhoops();
    const now = Date.now();
    shakeUntil = now + SHAKE_MS;
    flashUntil = now + FLASH_MS;
    const tip = tipY(snake);
    const b = birdCenter();
    const dx = b.x - snake.x;
    const dy = b.y - tip;
    const len = Math.max(1, Math.hypot(dx, dy));
    recoilDx = (dx / len) * 24;
    recoilDy = (dy / len) * 16;
    recoilUntil = now + RECOIL_MS;
    xValue += recoilDx;
    yValue += recoilDy;

    // Hard: snake bites + lightning share the zap meter
    registerZap(now);
  }

  function playfieldEdges() {
    return {
      left: contained ? boardW * 0.04 : boardW / 10,
      right: boardW - boardW / 11,
      top: contained ? boardH * 0.04 : boardH / 20,
      bottom: contained
        ? boardH - birdDrawSize().h * 0.9
        : boardH - boardH / 11,
    };
  }

  function clampBirdToPlayfield() {
    const e = playfieldEdges();
    if (xValue < e.left) xValue = e.left;
    if (xValue > e.right) xValue = e.right;
    if (yValue < e.top) yValue = e.top;
    if (yValue > e.bottom) yValue = e.bottom;
  }

  function easeInOutCubic(t) {
    if (t < 0.5) return 4 * t * t * t;
    return 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function notifyDifficulty(id) {
    storeDifficulty(id);
    if (typeof onDifficultyChange === 'function') {
      try {
        onDifficultyChange(id);
      } catch {
        // ignore host callback errors
      }
    }
  }

  function clearSegmentEntities() {
    worms = [];
    snakes = [];
    rewardFlowers = [];
    lighthouse = null;
    beamIlluminated = false;
    dirt = [];
    nextWormAt = 0;
    nextSnakeAt = 0;
    nextFlowerAt = 0;
  }

  function reviveCoins() {
    for (let i = 0; i < coins.length; i += 1) {
      coins[i].alive = true;
    }
  }

  function enterSegment(nextIndex, nextDiff, birdX) {
    segmentIndex = nextIndex;
    difficulty = nextDiff;
    if (incomingWeather) {
      weather = incomingWeather;
      incomingWeather = null;
    } else {
      resetWeather(weather);
      ensureWeather(weather, difficulty, boardW, boardH);
    }
    clearSegmentEntities();
    zapCount = 0;
    isGameOver = false;
    gameOverUntil = 0;
    reviveCoins();
    const edges = playfieldEdges();
    xValue = birdX != null ? birdX : edges.left;
    facing = 1;
    if (playStartedAt) {
      nextProgressiveAt = Date.now() + difficulty.progressiveIntervalMs;
    }
    ensureWeather(weather, difficulty, boardW, boardH);
    ensureCreatures();
    notifyDifficulty(difficulty.id);
  }

  function finishSegmentSlide() {
    if (!slide) return;
    const { nextIndex, nextDiff, birdToX } = slide;
    slide = null;
    enterSegment(nextIndex, nextDiff, birdToX);
  }

  function beginSegmentSlide(nextIndex) {
    if (slide || isGameOver) return;
    const nextDiff = getDifficulty(DIFFICULTY_ORDER[nextIndex]);
    const edges = playfieldEdges();
    confirmAdvance();
    clearSegmentEntities();
    incomingWeather = createWeatherState();
    ensureWeather(incomingWeather, nextDiff, boardW, boardH);
    const duration = motionOk() ? SEGMENT_SLIDE_MS : 0;
    slide = {
      nextIndex,
      nextDiff,
      startAt: Date.now(),
      duration,
      birdFromX: xValue,
      birdToX: edges.left,
    };
    facing = 1;
    // Page wash / UI can switch as soon as the slide starts
    notifyDifficulty(nextDiff.id);
    if (duration <= 0) finishSegmentSlide();
  }

  /** Home: slide into the next difficulty; last segment exits to nextPage. */
  function tryAdvanceFromRightEdge() {
    if (!home) return false;
    if (slide) {
      xValue = playfieldEdges().right;
      return true;
    }
    if (segmentIndex < DIFFICULTY_ORDER.length - 1) {
      xValue = playfieldEdges().right;
      beginSegmentSlide(segmentIndex + 1);
      return true;
    }
    return false;
  }

  function slideProgress(now) {
    if (!slide) return 1;
    if (slide.duration <= 0) return 1;
    return Math.min(1, (now - slide.startAt) / slide.duration);
  }

  function updateSegmentSlide(now) {
    if (!slide) return;
    const u = easeInOutCubic(slideProgress(now));
    xValue = slide.birdFromX + (slide.birdToX - slide.birdFromX) * u;
    if (slideProgress(now) >= 1) finishSegmentSlide();
  }

  /** Medium/Hard: a wind gust pushes the bird one step toward the start. */
  function applyWindGust(fromMove) {
    if (!home || slide || !difficultyHasWind(difficulty) || isGameOver) return;
    const now = Date.now();
    const reduced = !motionOk();
    const { gust } = fromMove
      ? tickWindGust(weather, difficulty, now, reduced)
      : tickAmbientWind(weather, difficulty, now, reduced);
    if (!gust) return;
    facing = -1;
    xValue -= moveStepX();
    clampBirdToPlayfield();
  }

  function drawHud() {
    if (!home) return;
    const hasCombo = combo > 1;
    const padX = 14;
    const padY = 8;
    const boxX = 10;
    const boxY = 8;
    const label = 'Score  ' + score;
    const comboLabel = hasCombo ? 'Combo  x' + combo : '';

    p.push();
    p.textAlign(p.LEFT, p.TOP);
    p.textSize(18);
    const scoreW = p.textWidth(label);
    let boxW = scoreW + padX * 2;
    let boxH = 18 + padY * 2;
    if (hasCombo) {
      p.textSize(13);
      boxW = Math.max(boxW, p.textWidth(comboLabel) + padX * 2);
      boxH += 18;
    }

    // Framed score plaque
    p.noStroke();
    p.fill(255, 252, 248, 230);
    p.rect(boxX, boxY, boxW, boxH, 6);
    p.stroke(8, 116, 17, 200);
    p.strokeWeight(2.5);
    p.noFill();
    p.rect(boxX, boxY, boxW, boxH, 6);
    p.stroke(8, 116, 17, 70);
    p.strokeWeight(1);
    p.rect(boxX + 3, boxY + 3, boxW - 6, boxH - 6, 4);

    p.noStroke();
    p.fill(20, 70, 30, 245);
    p.textStyle(p.BOLD);
    p.textSize(18);
    p.text(label, boxX + padX, boxY + padY);
    if (hasCombo) {
      p.textStyle(p.NORMAL);
      p.textSize(13);
      p.fill(8, 116, 17, 220);
      p.text(comboLabel, boxX + padX, boxY + padY + 20);
    }
    p.textStyle(p.NORMAL);
    p.pop();
  }

  function drawZapMeter() {
    if (!home || !difficulty.maxZaps) return;
    const max = difficulty.maxZaps;
    const startX = boardW - 18 - max * 22;
    const y = 16;
    p.push();
    p.noStroke();
    p.textAlign(p.RIGHT, p.TOP);
    p.textSize(11);
    p.fill(58, 57, 57, 160);
    p.text('Zaps', startX - 8, y);
    for (let i = 0; i < max; i += 1) {
      const x = startX + i * 22;
      const used = i < zapCount;
      // bolt body
      p.fill(used ? 220 : 200, used ? 60 : 200, used ? 50 : 200, used ? 230 : 100);
      p.beginShape();
      p.vertex(x + 6, y);
      p.vertex(x + 14, y);
      p.vertex(x + 10, y + 10);
      p.vertex(x + 16, y + 10);
      p.vertex(x + 4, y + 24);
      p.vertex(x + 8, y + 12);
      p.vertex(x + 2, y + 12);
      p.endShape(p.CLOSE);
    }
    p.pop();
  }

  function drawGameOver(now) {
    if (!isGameOver) return;
    const left = Math.max(0, gameOverUntil - now);
    const fade = Math.min(1, (GAME_OVER_MS - left) / 200);
    p.push();
    p.noStroke();
    p.fill(40, 20, 20, Math.floor(150 * fade));
    p.rect(0, 0, boardW, boardH);
    p.fill(255, 240, 235, Math.floor(255 * fade));
    p.textAlign(p.CENTER, p.CENTER);
    p.textSize(Math.min(42, boardW * 0.08));
    p.text('Game Over', boardW / 2, boardH / 2 - 12);
    p.textSize(Math.min(16, boardW * 0.035));
    p.fill(255, 220, 210, Math.floor(220 * fade));
    p.text(
      difficulty.maxZaps + ' zaps — restarting…',
      boardW / 2,
      boardH / 2 + 22
    );
    p.pop();
    if (now >= gameOverUntil) {
      restartRun();
    }
  }

  function updateProgressiveDifficulty() {
    if (!home || !playStartedAt) return;
    const now = Date.now();
    while (now >= nextProgressiveAt) {
      progressiveBonus += difficulty.progressiveScoreBump;
      nextProgressiveAt += difficulty.progressiveIntervalMs;
    }
  }

  function maybeSpawnCreatures(now) {
    if (!home || slide) return;
    ensureCreatures();

    if (now >= nextWormAt && worms.length < difficulty.wormMax) {
      worms.push(spawnCreature('worm'));
      nextWormAt =
        now + difficulty.wormSpawnIntervalMs * (0.75 + Math.random() * 0.5);
    } else if (now >= nextWormAt) {
      nextWormAt = now + difficulty.wormSpawnIntervalMs * 0.4;
    }

    if (now >= nextSnakeAt && snakes.length < difficulty.snakeMax) {
      snakes.push(spawnCreature('snake'));
      nextSnakeAt =
        now + difficulty.snakeSpawnIntervalMs * (0.7 + Math.random() * 0.55);
    } else if (now >= nextSnakeAt) {
      nextSnakeAt = now + difficulty.snakeSpawnIntervalMs * 0.35;
    }

    const flowerMax = difficulty.flowerMax || 0;
    if (flowerMax > 0) {
      if (now >= nextFlowerAt && rewardFlowers.length < flowerMax) {
        rewardFlowers.push(spawnRewardFlower());
        nextFlowerAt =
          now +
          (difficulty.flowerSpawnIntervalMs || 4000) * (0.75 + Math.random() * 0.5);
      } else if (now >= nextFlowerAt) {
        nextFlowerAt =
          now + (difficulty.flowerSpawnIntervalMs || 4000) * 0.4;
      }
    }
  }

  function updateCreatures(now) {
    if (!home) return;
    const reducedMotion = !motionOk();
    const wormCfg = {
      emergeMs: difficulty.wormEmergeMs,
      visibleMs: difficulty.wormVisibleMs,
      now,
      reducedMotion,
    };
    const snakeCfg = {
      emergeMs: difficulty.snakeEmergeMs,
      visibleMs: difficulty.snakeVisibleMs,
      now,
      reducedMotion,
    };

    const aliveWorms = [];
    for (let i = 0; i < worms.length; i += 1) {
      const w = worms[i];
      const spawned = tickCreature(w, wormCfg);
      if (spawned.length) dirt.push(...spawned);
      if (w.state !== 'gone') aliveWorms.push(w);
    }
    worms = aliveWorms;

    const aliveSnakes = [];
    for (let i = 0; i < snakes.length; i += 1) {
      const s = snakes[i];
      const spawned = tickCreature(s, snakeCfg);
      if (spawned.length) dirt.push(...spawned);
      if (s.state !== 'gone') aliveSnakes.push(s);
    }
    snakes = aliveSnakes;

    if (difficulty.flowerMax) {
      const flowerCfg = {
        emergeMs: difficulty.flowerEmergeMs || 600,
        visibleMs: difficulty.flowerVisibleMs || 2800,
        now,
        reducedMotion,
      };
      const aliveFlowers = [];
      for (let i = 0; i < rewardFlowers.length; i += 1) {
        const f = rewardFlowers[i];
        tickRewardFlower(f, flowerCfg);
        if (f.state !== 'gone') aliveFlowers.push(f);
      }
      rewardFlowers = aliveFlowers;
    } else {
      rewardFlowers = [];
    }

    if (lighthouse) {
      const b = birdCenter();
      const { recovered, illuminated } = tickLighthouse(
        lighthouse,
        now,
        reducedMotion,
        b.x,
        b.y,
        {
          windGust: !!(weather.windLines && weather.windLines.length > 0),
          cooldownMs: difficulty.beamCooldownMs || 8500,
          healMs: difficulty.beamHealMs || 900,
        }
      );
      beamIlluminated = illuminated;
      if (recovered) onBeamRecovery();
    } else {
      beamIlluminated = false;
    }
    dirt = updateDirt(dirt);
  }

  function checkCreatureCollisions() {
    if (!home || isGameOver || slide) return;
    const pad = hitPadCreature();
    const b = birdCenter();
    const now = Date.now();

    // Brief sanctuary while the beam's warmth is still on the bird
    if (lighthouse && now < lighthouse.healingUntil) return;

    for (let i = 0; i < rewardFlowers.length; i += 1) {
      if (isNearRewardFlower(b.x, b.y, rewardFlowers[i], pad * 0.9)) {
        onFlowerCollected(rewardFlowers[i]);
        return;
      }
    }
    for (let i = 0; i < worms.length; i += 1) {
      if (isNearCreature(b.x, b.y, worms[i], pad)) {
        onWormCollected(worms[i]);
        return;
      }
    }
    for (let i = 0; i < snakes.length; i += 1) {
      if (isNearCreature(b.x, b.y, snakes[i], pad)) {
        onSnakeHit(snakes[i]);
        return;
      }
    }
  }

  function markBirdWon() {
    try {
      sessionStorage.setItem(BIRD_WON_KEY, '1');
    } catch {
      // ignore
    }
  }

  function consumeBirdWon() {
    try {
      if (sessionStorage.getItem(BIRD_WON_KEY) === '1') {
        sessionStorage.removeItem(BIRD_WON_KEY);
        return true;
      }
    } catch {
      // ignore
    }
    return false;
  }

  function spawnBurst(cx, cy, count) {
    const color =
      FIREWORK_COLORS[Math.floor(Math.random() * FIREWORK_COLORS.length)];
    for (let i = 0; i < count; i += 1) {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.4;
      const speed = 2.2 + Math.random() * 5.5;
      sparks.push({
        x: cx,
        y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 1.5,
        life: 1,
        decay: 0.012 + Math.random() * 0.018,
        size: 2.5 + Math.random() * 3.5,
        color,
        gravity: 0.06 + Math.random() * 0.04,
      });
    }
  }

  function startCelebration() {
    if (celebrated || !motionOk()) {
      if (!celebrated) {
        celebrated = true;
        celebrateWin();
      }
      return;
    }
    celebrated = true;
    celebrationUntil = Date.now() + CELEBRATION_MS;
    nextBurstAt = Date.now();
    celebrateWin();
    spawnBurst(xValue + birdDrawSize().w / 2, yValue + birdDrawSize().h / 2, 36);
    spawnBurst(boardW * 0.35, boardH * 0.28, 28);
    spawnBurst(boardW * 0.65, boardH * 0.22, 28);
  }

  function updateCelebration() {
    if (!celebrated || Date.now() > celebrationUntil) return;
    const now = Date.now();
    if (now >= nextBurstAt) {
      const cx = boardW * (0.2 + Math.random() * 0.6);
      const cy = boardH * (0.15 + Math.random() * 0.35);
      spawnBurst(cx, cy, 22 + Math.floor(Math.random() * 16));
      nextBurstAt = now + 380 + Math.random() * 420;
    }
  }

  function drawCelebration() {
    if (!sparks.length) return;
    p.push();
    p.noStroke();
    const next = [];
    for (let i = 0; i < sparks.length; i += 1) {
      const s = sparks[i];
      s.x += s.vx;
      s.y += s.vy;
      s.vy += s.gravity;
      s.vx *= 0.99;
      s.life -= s.decay;
      if (s.life <= 0.02) continue;
      const [r, g, b] = s.color;
      p.fill(r, g, b, Math.floor(255 * Math.min(1, s.life)));
      p.circle(s.x, s.y, s.size * (0.6 + s.life * 0.6));
      next.push(s);
    }
    sparks = next;
    p.pop();
  }

  function drawFeedbackFx(now) {
    if (now < shakeUntil && motionOk()) {
      const mag = 5 * ((shakeUntil - now) / SHAKE_MS);
      p.translate((Math.random() - 0.5) * mag * 2, (Math.random() - 0.5) * mag * 2);
    }
    if (now < flashUntil) {
      const a = Math.floor(90 * ((flashUntil - now) / FLASH_MS));
      p.push();
      p.noStroke();
      p.fill(220, 40, 40, a);
      p.rect(0, 0, boardW, boardH);
      p.pop();
    }
  }

  function applyProps(props) {
    if (!props) return;
    if (props.nextPage) nextPage = props.nextPage;
    else if (props.gameWon) {
      gameWon = true;
      if (!celebrated && consumeBirdWon()) startCelebration();
    }
    if (props.extension) extension = true;
    if (props.contained) contained = true;
    if (props.home) home = true;
    if (typeof props.onDifficultyChange === 'function') {
      onDifficultyChange = props.onDifficultyChange;
    }

    if (props.contained || props.home) {
      syncBoardSize(true);
      if (props.home && !slide) ensureCreatures();
    }

    // Continuous adventure owns difficulty in-sketch; React only mirrors for CSS/UI.
    // Ignore prop hot-swaps on home so slide callbacks don't reset the run.
    if (props.difficulty && !home) {
      const next = getDifficulty(props.difficulty);
      if (next.id !== difficulty.id) {
        difficulty = next;
        segmentIndex = Math.max(0, DIFFICULTY_ORDER.indexOf(difficulty.id));
        const now = Date.now();
        if (playStartedAt) {
          nextProgressiveAt = now + difficulty.progressiveIntervalMs;
        }
        clearSegmentEntities();
        zapCount = 0;
        isGameOver = false;
        gameOverUntil = 0;
        resetWeather(weather);
        ensureWeather(weather, difficulty, boardW, boardH);
        ensureCreatures();
      }
    }
  }

  p.setup = () => {
    if (document.querySelector('.home-game-frame')) contained = true;
    const { w, h } = measureBoard();
    boardW = w;
    boardH = h;
    layoutPlayfield();
    p.createCanvas(boardW, boardH);
    p.noStroke();
    requestAnimationFrame(function () {
      syncBoardSize(true);
      ensureCreatures();
      requestAnimationFrame(function () {
        syncBoardSize(true);
        ensureCreatures();
      });
    });
  };

  p.windowResized = () => {
    syncBoardSize(true);
    p.redraw();
  };

  p.draw = () => {
    if (contained && p.frameCount % 45 === 1) syncBoardSize(false);

    p.clear();
    const t = p.frameCount;
    const now = Date.now();
    const reducedMotion = !motionOk();

    if (home && !slide) {
      ensureWeather(weather, difficulty, boardW, boardH);
    }

    if (slide) {
      updateSegmentSlide(now);
    } else if (!isGameOver) {
      updateProgressiveDifficulty();
      maybeSpawnCreatures(now);
      updateCreatures(now);
      // Timed wind — does not wait on keypresses
      if (home && difficultyHasWind(difficulty)) applyWindGust(false);
      if (home && difficulty.weather === 'storm') {
        const b = birdCenter();
        const { lightningHit } = tickStorm(
          weather,
          difficulty,
          boardW,
          boardH,
          b.x,
          b.y,
          now,
          reducedMotion
        );
        if (lightningHit) onLightningHit();
      }
    } else {
      updateCreatures(now);
      if (home && difficulty.weather === 'storm') {
        // Keep rain/clouds moving during game-over beat
        const b = birdCenter();
        tickStorm(
          weather,
          difficulty,
          boardW,
          boardH,
          b.x,
          b.y,
          now,
          reducedMotion
        );
      }
    }

    p.push();
    drawFeedbackFx(now);

    if (home && slide && slide.nextDiff && incomingWeather) {
      const u = easeInOutCubic(slideProgress(now));
      const outX = -boardW * u;
      const inX = boardW * (1 - u);

      p.push();
      p.translate(outX, 0);
      drawWeather(
        p,
        weather,
        difficulty,
        boardW,
        boardH,
        t,
        now,
        reducedMotion,
        p.mouseX - outX,
        p.mouseY
      );
      p.pop();

      p.push();
      p.translate(inX, 0);
      drawWeather(
        p,
        incomingWeather,
        slide.nextDiff,
        boardW,
        boardH,
        t,
        now,
        reducedMotion,
        p.mouseX - inX,
        p.mouseY
      );
      p.pop();
    } else if (home) {
      drawWeather(
        p,
        weather,
        difficulty,
        boardW,
        boardH,
        t,
        now,
        reducedMotion,
        p.mouseX,
        p.mouseY
      );

      if (lighthouse) {
        let lightningFlash = 0;
        if (weather.lightning && now < weather.lightning.until) {
          lightningFlash =
            (weather.lightning.until - now) /
            (difficulty.lightningFlashMs || 280);
        }
        drawLighthouse(p, lighthouse, now, t, {
          lightningFlash,
          illuminated: beamIlluminated,
          healing: now < lighthouse.healingUntil,
          boardW,
          boardH,
        });
      }
      for (let i = 0; i < rewardFlowers.length; i += 1) {
        drawRewardFlower(p, rewardFlowers[i], t);
      }
      for (let i = 0; i < worms.length; i += 1) drawCreature(p, worms[i], t);
      for (let i = 0; i < snakes.length; i += 1) drawCreature(p, snakes[i], t);
      drawDirt(p, dirt);

      for (let i = 0; i < coins.length; i += 1) {
        const c = coins[i];
        if (!c.alive) continue;
        drawProcCoin(p, c.x, c.y, coinSize, t, c.phase);
      }
    }

    const bob = Math.sin(t * BIRD_BOB_SPEED) * BIRD_BOB_AMP;
    const scale = 1 + Math.sin(t * BIRD_SCALE_SPEED) * BIRD_SCALE_AMP;
    const size = birdDrawSize();
    let drawX = xValue;
    let drawY = yValue + bob;
    if (now < recoilUntil && motionOk()) {
      const k = (recoilUntil - now) / RECOIL_MS;
      drawX += recoilDx * k * 0.25;
      drawY += recoilDy * k * 0.25;
    }
    if (home && lighthouse) {
      const healStr =
        now < lighthouse.healingUntil
          ? (lighthouse.healingUntil - now) / (difficulty.beamHealMs || 900)
          : 0;
      const glow = Math.max(healStr, beamIlluminated ? 0.55 : 0);
      if (glow > 0.05) {
        drawBirdBeaconGlow(
          p,
          drawX + (size.w * scale) / 2,
          drawY + (size.h * scale) / 2,
          size.w * scale,
          size.h * scale,
          glow
        );
      }
    }
    drawProcBird(
      p,
      drawX,
      drawY,
      size.w * scale,
      size.h * scale,
      facing,
      t
    );

    if (home && playStartedAt && !isGameOver && !slide) {
      collectCoins();
      checkCreatureCollisions();
    }

    drawHud();
    drawZapMeter();
    updateCelebration();
    drawCelebration();
    drawGameOver(now);
    p.pop();
  };

  function pointerPlayCoords() {
    if (p.touches && p.touches.length > 0) {
      return { x: p.touches[0].x, y: p.touches[0].y };
    }
    return { x: p.mouseX, y: p.mouseY };
  }

  /** Tap/click relative to the bird → one keyboard-sized step. */
  function directionFromPointer(px, py) {
    const b = birdCenter();
    const dx = px - b.x;
    const dy = py - b.y;
    const size = birdDrawSize();
    const dead = Math.max(28, Math.min(size.w, size.h) * 0.45);
    if (Math.hypot(dx, dy) < dead) return null;
    if (Math.abs(dx) >= Math.abs(dy)) return dx < 0 ? 'left' : 'right';
    return dy < 0 ? 'up' : 'down';
  }

  function tryMove(dir) {
    if (!dir || isGameOver || slide) return false;

    getAdventureMusicEngine().unlockFromGesture();
    if (contained) syncBoardSize(false);
    else {
      const { w, h } = measureBoard();
      boardW = w;
      boardH = h;
    }
    ensurePlayClock();

    const stepX = moveStepX();
    const stepY = moveStepY();
    const prevX = xValue;
    const prevY = yValue;
    let didMove = false;

    if (dir === 'left') {
      facing = -1;
      xValue -= stepX;
      didMove = true;
    } else if (dir === 'right') {
      facing = 1;
      xValue += stepX;
      didMove = true;
    } else if (dir === 'up') {
      yValue -= stepY;
      didMove = true;
    } else if (dir === 'down') {
      yValue += stepY;
      didMove = true;
    }

    if (!didMove) return false;

    const edges = playfieldEdges();

    if (xValue > edges.right) {
      if (!tryAdvanceFromRightEdge()) {
        if (!didAdvance) {
          didAdvance = true;
          if (nextPage === '/contact') markBirdWon();
          else confirmAdvance();
        }
        history.push(nextPage);
        if (gameWon) xValue -= stepX;
      }
    } else if (xValue < edges.left) {
      xValue = edges.left;
    }

    if (yValue > edges.bottom) yValue = edges.bottom;
    else if (yValue < edges.top) yValue = edges.top;

    if (home) applyWindGust(true);

    if (home) {
      collectCoins(prevX, prevY);
      checkCreatureCollisions();
    }

    return true;
  }

  function handlePointerSteer() {
    if (isGameOver || slide) return false;
    const pt = pointerPlayCoords();
    if (
      pt.x < 0 ||
      pt.y < 0 ||
      pt.x > boardW ||
      pt.y > boardH
    ) {
      return false;
    }
    return tryMove(directionFromPointer(pt.x, pt.y));
  }

  let suppressMouseSteerUntil = 0;

  p.keyPressed = () => {
    if (p.keyCode === p.LEFT_ARROW) tryMove('left');
    else if (p.keyCode === p.RIGHT_ARROW) tryMove('right');
    else if (p.keyCode === p.UP_ARROW) tryMove('up');
    else if (p.keyCode === p.DOWN_ARROW) tryMove('down');
    else getAdventureMusicEngine().unlockFromGesture();
  };

  // iPad / touch: tap above/below/left/right of the bird = one step
  p.touchStarted = () => {
    handlePointerSteer();
    suppressMouseSteerUntil = Date.now() + 450;
    return false;
  };

  p.touchMoved = () => false;

  p.mousePressed = () => {
    if (Date.now() < suppressMouseSteerUntil) return;
    handlePointerSteer();
  };

  p.myCustomRedrawAccordingToNewPropsHandler = function (props) {
    applyProps(props);
  };
}
